/**
 * Short audio cues (timer / buzzer signals) played through one shared Web Audio
 * context.
 *
 * `new Audio(src).play()` is only allowed inside a user gesture in Safari (and
 * on mobile browsers), so cues fired from a timer tick or a SignalR event were
 * silently rejected — only the Start click was ever heard. A Web Audio context
 * resumed once during a gesture can play decoded buffers at any later time.
 */

type AudioContextCtor = typeof AudioContext;

// Cues are cut to this length; the source files run a little longer.
const MAX_CUE_SECONDS = 1;
const FADE_OUT_SECONDS = 0.05;

const getAudioContextCtor = (): AudioContextCtor | null => {
  if (typeof window === 'undefined') return null;
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext ?? null;
};

let context: AudioContext | null = null;
const buffers = new Map<string, Promise<AudioBuffer | null>>();

const getContext = (): AudioContext | null => {
  if (context) return context;
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    context = new Ctor();
  } catch {
    context = null;
  }
  return context;
};

const loadBuffer = (ctx: AudioContext, src: string): Promise<AudioBuffer | null> => {
  let pending = buffers.get(src);
  if (!pending) {
    pending = fetch(src)
      .then(res => res.arrayBuffer())
      // Callback form: older Safari has no promise-returning decodeAudioData.
      .then(data => new Promise<AudioBuffer>((resolve, reject) => ctx.decodeAudioData(data, resolve, reject)))
      .catch(() => {
        buffers.delete(src);
        return null;
      });
    buffers.set(src, pending);
  }
  return pending;
};

/** Resume the audio context. Must run inside a user gesture to take effect. */
export const unlockSoundCues = (): void => {
  const ctx = getContext();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
};

/** Fetch and decode cues ahead of time so the first play has no delay. */
export const preloadSoundCues = (...sources: string[]): void => {
  const ctx = getContext();
  if (!ctx) return;
  sources.forEach(src => { loadBuffer(ctx, src); });
};

const playWithElement = (src: string) => {
  try {
    const audio = new Audio(src);
    audio.play().catch(() => {});
    window.setTimeout(() => audio.pause(), MAX_CUE_SECONDS * 1000);
  } catch {
    // noop
  }
};

export const playSoundCue = (src: string): void => {
  const ctx = getContext();
  if (!ctx) {
    playWithElement(src);
    return;
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  loadBuffer(ctx, src).then(buffer => {
    if (!buffer) {
      playWithElement(src);
      return;
    }
    const node = ctx.createBufferSource();
    node.buffer = buffer;
    // Short fade before the cut so stopping mid-tone doesn't click.
    const gain = ctx.createGain();
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(1, now + MAX_CUE_SECONDS - FADE_OUT_SECONDS);
    gain.gain.linearRampToValueAtTime(0, now + MAX_CUE_SECONDS);
    node.connect(gain).connect(ctx.destination);
    node.start(now);
    node.stop(now + MAX_CUE_SECONDS);
  });
};

// Any interaction with the page unlocks audio, so cues fired later by a timer
// or a remote event (buzzer press) are allowed to play.
if (typeof window !== 'undefined') {
  const unlockOnGesture = () => {
    unlockSoundCues();
    if (context?.state === 'running') {
      window.removeEventListener('pointerdown', unlockOnGesture, true);
      window.removeEventListener('keydown', unlockOnGesture, true);
    }
  };
  window.addEventListener('pointerdown', unlockOnGesture, true);
  window.addEventListener('keydown', unlockOnGesture, true);
}
